import { PROFILES_MUTATIONS } from '@/state/mutation-types'
import { PROFILES_ACTIONS } from '@/state/action-types'
import { users as freelancers } from '@/api/vue-api'

// Creates a string from the filter state that we use for our caching
function createHash(filter) {
  return `_s${filter.search.term}c${filter.categories.selected}e${filter.experience.selected}m${
    filter.markets.selected[0]
  }r${filter.rates.selected}a${filter.regions.selected.join()}sb${filter.sortBy}ma${
    filter.match
  }eg${filter.engaged.selected}o${filter.own}d${filter.divisions.selected}`
}

export default {
  strict: import.meta.env.VITE_APP_ENV !== 'production',
  state: {
    cache: {},
    key: null,
    page: 1,
    pending: 0,
  },
  getters: {
    // Gets the current filter state profiles
    profiles: (state) => state.cache[state.key],

    // Returns the current computed key that is used to access the cached profiles
    profilesKey: (state) => state.key,

    // Since we use a paginated search structure we would like to know when we have gotten all profiles for a certain
    // filter state. This is used to determine if we show a loading animation or show that we have reached the end.
    hasAllProfiles: (state) => {
      if (state.cache[state.key] && state.cache[state.key].items) {
        return state.cache[state.key].items.length - parseInt(state.cache[state.key].count, 10) >= 0
      }
      return false
    },

    // Boolean that indicates if we are currently searching for profiles
    profilesSearching: (state) => state.pending > 0,
  },
  actions: {
    /**
     * This action prepares the filter and cache for searching and then dispatches a search query to the backend,
     * and then will resolve with an array of profiles when it has finished. Otherwise it will resolve with 'searching'
     * @returns {Promise<any>}
     */
    [PROFILES_ACTIONS.SEARCH_PROFILES]({ commit, state, rootGetters }) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        let response = {}
        const { filter } = rootGetters

        if (!filter.search.term || filter.search.term == null) {
          filter.search.term = ''
        }

        // Here we create the key we will use for our cache

        const cacheKey = createHash(filter)
        commit(PROFILES_MUTATIONS.SET_KEY, cacheKey)

        if (
          !state.cache[cacheKey] ||
          (state.cache[cacheKey].page !== undefined && state.cache[cacheKey].page !== state.page)
        ) {
          if (state.cache[cacheKey] === undefined) {
            commit(PROFILES_MUTATIONS.RESET_PAGE_NUMBER)
            // We set the status to searching so we won't be doing it twice at the same time
            commit(PROFILES_MUTATIONS.ADD_TO_CACHE, {
              data: 'searching',
              key: cacheKey,
              page: 1,
            })
          }
          const { page } = state
          // Start getting the data
          try {
            commit(PROFILES_MUTATIONS.REQUEST_SENT)
            response = await freelancers.getFeed({
              s: filter.search.term,
              exp: filter.experience.selected,
              rate: filter.rates.selected,
              areas: filter.regions.selected.join(),
              market: filter.markets.selected[0],
              sort: filter.sortBy,
              page,
              category_id: filter.categories.selected,
              match: filter.match,
              engaged: filter.engaged.selected,
              own: filter.own,
              division: filter.divisions.selected,
            })

            // We cache the response
            commit(PROFILES_MUTATIONS.ADD_TO_CACHE, {
              data: response.data,
              page,
              key: cacheKey,
            })
            commit(PROFILES_MUTATIONS.RESPONSE_RECEIVED)
            resolve(response.data)
          } catch (e) {
            reject(e)
            commit(PROFILES_MUTATIONS.RESPONSE_RECEIVED)
          }
        } else if (state.cache[cacheKey].items === 'searching') {
          resolve('searching')
        } else {
          response.data = state.cache[cacheKey]
          if (state.cache[cacheKey].page !== undefined) {
            commit(PROFILES_MUTATIONS.RESET_PAGE_NUMBER, state.cache[cacheKey].page)
          }
        }

        resolve(response.data)
      })
    },

    /**
     * This action simply increments the page number and then dispatches a new search action
     * This is an action that is made to simplify when to fetch a new page of profiles
     * @returns {Promise<any>}
     */
    [PROFILES_ACTIONS.NEXT_PAGE]({ commit, state, dispatch }) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        if (state.cache[state.key].page === state.page) {
          commit(PROFILES_MUTATIONS.NEXT_PAGE)
          try {
            dispatch(PROFILES_ACTIONS.SEARCH_PROFILES)
            resolve()
          } catch (e) {
            reject(e)
          }
        }
      })
    },
  },
  mutations: {
    // Mutation that adds a new instance to the cache or updates the current entry in that key depending on the incoming data
    [PROFILES_MUTATIONS.ADD_TO_CACHE](state, list) {
      if (state.cache[list.key] && state.cache[list.key].items !== 'searching') {
        if (!state.cache[list.key].pages.includes(list.page)) {
          state.cache[list.key].items = state.cache[list.key].items.concat(list.data.items)
          state.cache[list.key].page = list.page
          state.cache[list.key].pages.push(list.page)
        }
      } else if (list.data === 'searching') {
        state.cache[list.key] = {
          items: list.data,
          page: list.page,
        }
      } else {
        state.cache[list.key] = {
          items: list.data.items,
          count: list.data.count,
          all_profile_ids: list.data.all_profile_ids,
          page: list.page,
          pages: [list.page],
        }
      }
    },
    [PROFILES_MUTATIONS.SET_KEY](state, key) {
      state.key = key
    },
    [PROFILES_MUTATIONS.NEXT_PAGE](state) {
      state.page += 1
    },
    [PROFILES_MUTATIONS.RESET_PAGE_NUMBER](state, page = 1) {
      state.page = page
    },
    [PROFILES_MUTATIONS.REQUEST_SENT](state) {
      state.pending++
    },
    [PROFILES_MUTATIONS.RESPONSE_RECEIVED](state) {
      state.pending--
    },
  },
}
